export type DueAtInputType = 'date' | 'datetime-local';

/** True when the ISO value includes a non-midnight clock time. */
export function hasSpecificDueTime(iso: string | null | undefined): boolean {
  if (!iso?.trim()) return false;

  const trimmed = iso.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return false;

  const timeMatch = trimmed.match(/T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!timeMatch) return false;

  const hours = Number(timeMatch[1]);
  const minutes = Number(timeMatch[2]);
  const seconds = timeMatch[3] ? Number(timeMatch[3]) : 0;
  return !(hours === 0 && minutes === 0 && seconds === 0);
}

export function toDateInputValue(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';

  const trimmed = iso.trim();
  const dateOnly = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (dateOnly) return dateOnly[1];

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function toDatetimeLocalInputValue(iso: string | null | undefined): string {
  if (!iso?.trim()) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (value: number) => String(value).padStart(2, '0');
  return [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())]
    .join('-')
    .concat(`T${pad(date.getHours())}:${pad(date.getMinutes())}`);
}

export function defaultDueAtFields(
  suggestedDueAt: string | null,
  confidence: string,
): { dueAt: string; dueAtInputType: DueAtInputType } {
  if (confidence !== 'high' && confidence !== 'medium') {
    return { dueAt: '', dueAtInputType: 'date' };
  }

  if (!suggestedDueAt?.trim()) {
    return { dueAt: '', dueAtInputType: 'date' };
  }

  if (hasSpecificDueTime(suggestedDueAt)) {
    return {
      dueAt: toDatetimeLocalInputValue(suggestedDueAt),
      dueAtInputType: 'datetime-local',
    };
  }

  return {
    dueAt: toDateInputValue(suggestedDueAt),
    dueAtInputType: 'date',
  };
}

export function dueAtFieldsToIso(dueAt: string, inputType: DueAtInputType): string | null {
  if (!dueAt.trim()) return null;

  if (inputType === 'datetime-local') {
    const parsed = new Date(dueAt);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString();
  }

  const parsed = new Date(`${dueAt}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}
